// components/industry-templates/FoodEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChefHat, UtensilsCrossed, Wine, Trophy } from 'lucide-react';

export const FoodEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Celebrity Chefs */}
      {industryData?.celebrity_chefs && (
        <Card className="border-0 bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <ChefHat className="w-8 h-8" style={{ color: theme.primary }} />
              Celebrity Chefs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.celebrity_chefs.map((chef: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">👨‍🍳</div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-1">{chef.name}</h3>
                      <p className="text-slate-300 mb-2">{chef.specialty}</p>
                      {chef.michelin_stars && (
                        <div className="flex gap-1 mb-2">
                          {[...Array(chef.michelin_stars)].map((_, i) => (
                            <span key={i} className="text-yellow-500 text-xl">⭐</span>
                          ))}
                        </div>
                      )}
                      {chef.demonstration && (
                        <Badge style={{ backgroundColor: theme.primary }}>
                          Demo: {chef.demonstration}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cuisine Types */}
      {industryData?.cuisine_types && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <UtensilsCrossed className="w-6 h-6" style={{ color: theme.primary }} />
              Cuisines & Food Stalls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {industryData.cuisine_types.map((cuisine: string, idx: number) => (
                <div key={idx} className="p-3 bg-white/5 rounded-lg text-center border border-white/10 hover:border-orange-500/50 transition-all">
                  <p className="text-white font-medium text-sm">{cuisine}</p>
                </div>
              ))}
            </div>
            {industryData.food_stalls && (
              <p className="text-center text-slate-400 mt-4">
                <span className="font-bold text-2xl" style={{ color: theme.primary }}>{industryData.food_stalls}+</span> Food Stalls
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Wine Tasting */}
      {industryData?.wine_tasting && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wine className="w-6 h-6" style={{ color: theme.primary }} />
              Wine Tasting Experience
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white mb-2">Sommelier-guided wine tasting featuring:</p>
              <div className="flex flex-wrap gap-2">
                {industryData.wine_tasting.varieties?.map((variety: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="border-white/20 text-white">
                    {variety}
                  </Badge>
                ))}
              </div>
              {industryData.wine_tasting.price && (
                <p className="text-sm text-slate-400 mt-3">Price: ₹{industryData.wine_tasting.price}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Competitions */}
      {industryData?.food_competitions && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="w-6 h-6" style={{ color: theme.primary }} />
              Food Competitions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.food_competitions.map((comp: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-1">{comp.name}</h4>
                  <p className="text-sm text-slate-400">{comp.theme}</p>
                  <Badge className="mt-2 bg-yellow-500 text-black">Prize: {comp.prize}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
